import React, { useState, useRef, useEffect, memo, useCallback, useMemo } from 'react';
import { Box, Chip } from '@mui/material';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import { useDraggable } from '@dnd-kit/core';

// Precompute status mappings for faster lookups
const STATUS_MAPPINGS = {
    'B': 'Bank Holiday',
    'S': 'Sick Leave',
    'H': 'Holiday',
    'O': 'Day Off'
};

// Memoized status values and keys for faster lookups
const STATUS_VALUES = Object.values(STATUS_MAPPINGS);
const STATUS_KEYS = Object.keys(STATUS_MAPPINGS);

// Define event handlers outside the component
// Optimized input change handler with batched state updates
const createHandleInputChange = (
    inputs,
    setInputs,
    inputRefs,
    validateTime,
    formatTimeValue,
    setChip,
    data,
    onChange
) => (index, value) => {
    // Use functional updates to batch state changes
    setInputs(prevInputs => {
        const newInputs = [...prevInputs];

        // Helper function to check if we should create a chip
        const checkAndCreateChip = (inputs) => {
            console.log({ data })

            if (inputs[0] && inputs[1] && inputs[2] && inputs[3]) {
                const startHour = parseInt(inputs[0]);
                const startMinute = parseInt(inputs[1]);
                const endHour = parseInt(inputs[2]);
                const endMinute = parseInt(inputs[3]);

                // Validate time range with all the required constraints
                if (startHour >= 8 && startHour <= 21 && startMinute >= 0 && startMinute <= 59 &&
                    endHour >= 8 && endHour <= 22 && endMinute >= 0 && endMinute <= 59 &&
                    (endHour > startHour || (endHour === startHour && endMinute >= startMinute + 15)) &&
                    (startHour !== 21 || startMinute <= 45) &&
                    (endHour !== 22 || endMinute === 0)) {

                    const startTime = `${inputs[0].padStart(2, '0')}:${inputs[1].padStart(2, '0')}`;
                    const endTime = `${inputs[2].padStart(2, '0')}:${inputs[3].padStart(2, '0')}`;
                    const timeRange = `${startTime} - ${endTime}`;

                    // Set the chip and update the therapist data
                    setChip(timeRange);
                    console.log({ data })
                    if (data && data.therapist) {
                        if (!data.therapist.changed) {
                            data.therapist.old = { ...data.therapist };
                        }
                        data.therapist.value = timeRange;
                        data.therapist.availability = {
                            start: startTime,
                            end: endTime,
                        };
                        data.therapist.changed = true;
                        console.log({ data });
                        onChange(data.therapist, data.dateIndex);
                    }
                    return true;
                }
            }
            return false;
        };

        // HANDLE FIRST INPUT (START HOUR)
        if (index === 0) {
            // Handle special characters in first input (status codes)
            if (STATUS_KEYS.includes(value.toUpperCase()) && !/^\d/.test(prevInputs[0])) {
                setChip(STATUS_MAPPINGS[value.toUpperCase()]);
                if (data && data.therapist) {
                    if (!data.therapist.changed) {
                        data.therapist.old = { ...data.therapist };
                    }
                    data.therapist.value = STATUS_MAPPINGS[value.toUpperCase()];
                    data.therapist.availability = {
                        start: '08:00',
                        end: '18:00',
                    };
                    data.therapist.changed = true;
                }
                return prevInputs; // No change to inputs
            }

            // Only allow numbers for start hour
            if (!/^\d{0,2}$/.test(value)) {
                newInputs[index] = '';
                return newInputs;
            }

            // Auto-prefix with 0 if single digit > 2
            if (value.length === 1 && parseInt(value) > 2) {
                if (parseInt(`0${value}`) < 8 || parseInt(`0${value}`) > 21) {
                    newInputs[index] = '';
                    return newInputs;
                }
                newInputs[index] = `0${value}`;
                // Focus next input after state update
                setTimeout(() => inputRefs[index + 1].current.focus(), 0);
                return newInputs;
            }

            // Handle 2-digit hour entry
            if (value.length === 2) {
                if (parseInt(value) < 8 || parseInt(value) > 21) {
                    newInputs[index] = '';
                    return newInputs;
                } else {
                    // Valid 2-digit hour, move to next input
                    newInputs[index] = value;
                    // Focus next input after state update
                    setTimeout(() => inputRefs[index + 1].current.focus(), 0);
                    return newInputs;
                }
            }
        }

        // HANDLE SECOND INPUT (START MINUTES)
        else if (index === 1) {
            // Only allow numbers 00-59 for minutes
            if (!/^\d{0,2}$/.test(value)) {
                newInputs[index] = '';
                return newInputs;
            }

            const startHour = parseInt(newInputs[0]);
            const minuteValue = parseInt(value);

            // Special case: if start hour is 21, max minute is 45
            if (startHour === 21 && value.length > 0 && minuteValue > 45) {
                newInputs[index] = '45';
                setTimeout(() => inputRefs[index + 1].current.focus(), 0);
                return newInputs;
            }

            // Auto-prefix with 0 if single digit
            if (value.length === 1 && minuteValue > 5) {
                newInputs[index] = `0${value}`;
                // Focus next input after state update
                setTimeout(() => inputRefs[index + 1].current.focus(), 0);
                return newInputs;
            }

            // Auto-advance to next field when valid 2-digit entry
            if (value.length === 2 && minuteValue >= 0 && minuteValue <= 59) {
                newInputs[index] = value;
                // Focus next input after state update
                setTimeout(() => inputRefs[index + 1].current.focus(), 0);
                return newInputs;
            }
        }

        // HANDLE THIRD INPUT (END HOUR)
        else if (index === 2) {
            // Only allow numbers for end hour
            if (!/^\d{0,2}$/.test(value)) {
                newInputs[index] = '';
                return newInputs;
            }

            const startHour = parseInt(newInputs[0] || '0');
            const hourValue = parseInt(value);



            // Auto-prefix with 0 if single digit > 2
            if (value.length === 1 && hourValue > 2) {
                if (hourValue < 8) {
                    newInputs[index] = '';
                    return newInputs;
                }
                newInputs[index] = `0${value}`;
                // Focus next input after state update
                if (hourValue < startHour) {
                    newInputs[index] = newInputs[0];
                }
                setTimeout(() => inputRefs[index + 1].current.focus(), 0);
                return newInputs;
            }
            if (value.length === 2 && hourValue > 22) {
                newInputs[index] = '';
                return newInputs;
            }
            // Auto-advance to next field when valid 2-digit entry
            if (value.length === 2 && hourValue >= 8 && hourValue <= 22) {
                newInputs[index] = value;
                // Focus next input after state update
                setTimeout(() => inputRefs[index + 1].current.focus(), 0);
                return newInputs;
            }

            // End hour must be >= start hour
            if (value.length === 2 && hourValue < startHour) {
                newInputs[index] = newInputs[0]; // Set to start hour
                setTimeout(() => inputRefs[index + 1].current.focus(), 0);
                return newInputs;
            }

            if (value.length === 2 && hourValue === 22) {
                newInputs[index] = value;
                newInputs[3] = '00';
                checkAndCreateChip(newInputs);
                return newInputs;
            }
        }

        // HANDLE FOURTH INPUT (END MINUTES)
        else if (index === 3) {
            // Only allow numbers 00-59 for minutes
            if (!/^\d{0,2}$/.test(value)) {
                newInputs[index] = '';
                return newInputs;
            }

            const startHour = parseInt(newInputs[0] || '0');
            const startMinute = parseInt(newInputs[1] || '0');
            const endHour = parseInt(newInputs[2] || '0');
            const minuteValue = parseInt(value);

            const comparableStartTime = startHour * 60 + startMinute;
            const comparableEndTime = endHour * 60 + minuteValue;

            console.log({ comparableStartTime, comparableEndTime });
            // Special case: if end hour is 22, minute must be 00
            if (endHour === 22 && value.length > 0 && minuteValue > 0) {
                newInputs[index] = '00';
                // Check if we can create a chip
                const updatedInputs = [...newInputs.slice(0, 3), '00'];
                checkAndCreateChip(updatedInputs);
                return updatedInputs;
            }
            // Auto-prefix with 0 if single digit
            if (value.length === 1 && minuteValue > 5) {
                newInputs[index] = `0${value}`;
                // Check if we can create a chip
                const updatedInputs = [...newInputs.slice(0, 3), `0${value}`];
                checkAndCreateChip(updatedInputs);
                return updatedInputs;
            }

            // If start hour equals end hour, end minute must be >= start minute + 15
            if (value.length === 2 && comparableEndTime < comparableStartTime + 15) {
                newInputs[index] = '';
                return newInputs;
            }

            // Handle 2-digit minute entry
            if (value.length === 2 && minuteValue >= 0 && minuteValue <= 59) {
                newInputs[index] = value;
                // Check if we can create a chip
                checkAndCreateChip(newInputs);
                return newInputs;
            }
        }

        // Default case for any other changes
        newInputs[index] = value;
        return newInputs;
    });
};

const createHandlePaste = (
    setChip,
    data,
    onChange,
    setInputs
) => (_, event) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData('text').trim();

    // Check for status mappings
    const upperPastedText = pastedText.toUpperCase();
    if (upperPastedText.length > 0) {
        // More efficient lookup using precomputed keys
        for (const key of STATUS_KEYS) {
            if (upperPastedText === key || upperPastedText[0] === key) {
                setChip(STATUS_MAPPINGS[key]);
                if (data && data.therapist) {
                    if (!data.therapist.changed) {
                        data.therapist.old = { ...data.therapist };
                    }
                    data.therapist.value = STATUS_MAPPINGS[key];
                    data.therapist.availability = { start: '08:00', end: '18:00' };
                    data.therapist.changed = true;
                    onChange(data.therapist, data.dateIndex);
                }
                return;
            }
        }
    }

    // Check for time format: HH:MM - HH:MM
    const timeRangeRegex = /^(\d{1,2}):(\d{1,2})\s*-\s*(\d{1,2}):(\d{1,2})$/;
    const match = pastedText.match(timeRangeRegex);

    if (match) {
        const [, startHour, startMin, endHour, endMin] = match;

        // Validate the time values
        const sHour = parseInt(startHour);
        const sMin = parseInt(startMin);
        const eHour = parseInt(endHour);
        const eMin = parseInt(endMin);

        if (sHour >= 8 && sHour <= 21 && sMin >= 0 && sMin <= 59 &&
            eHour >= 8 && eHour <= 22 && eMin >= 0 && eMin <= 59 &&
            (eHour > sHour || (eHour === sHour && eMin >= sMin + 15)) &&
            (sHour !== 21 || sMin <= 45) &&
            (eHour !== 22 || eMin === 0)) {

            // Format the time values with leading zeros if needed
            const formattedStartHour = sHour.toString().padStart(2, '0');
            const formattedStartMin = sMin.toString().padStart(2, '0');
            const formattedEndHour = eHour.toString().padStart(2, '0');
            const formattedEndMin = eMin.toString().padStart(2, '0');

            // Set the formatted time as a chip
            const timeRange = `${formattedStartHour}:${formattedStartMin} - ${formattedEndHour}:${formattedEndMin}`;
            setChip(timeRange);

            // Also update the inputs array for consistency
            setInputs([formattedStartHour, formattedStartMin, formattedEndHour, formattedEndMin]);

            if (data && data.therapist) {
                if (!data.therapist.changed) {
                    data.therapist.old = { ...data.therapist };
                }
                data.therapist.value = timeRange;
                data.therapist.availability = {
                    start: `${formattedStartHour}:${formattedStartMin}`,
                    end: `${formattedEndHour}:${formattedEndMin}`,
                };
                data.therapist.changed = true;
                console.log({ data });
                onChange(data.therapist, data.dateIndex);
            }
            return;
        }
    }

    // Check for time format without colons: HHMM - HHMM
    const shortTimeRangeRegex = /^(\d{2})(\d{2})\s*-\s*(\d{2})(\d{2})$/;
    const shortMatch = pastedText.match(shortTimeRangeRegex);

    if (shortMatch) {
        const [, startHour, startMin, endHour, endMin] = shortMatch;

        // Validate the time values
        const sHour = parseInt(startHour);
        const sMin = parseInt(startMin);
        const eHour = parseInt(endHour);
        const eMin = parseInt(endMin);

        if (sHour >= 8 && sHour <= 21 && sMin >= 0 && sMin <= 59 &&
            eHour >= 8 && eHour <= 22 && eMin >= 0 && eMin <= 59 &&
            (eHour > sHour || (eHour === sHour && eMin >= sMin + 15)) &&
            (sHour !== 21 || sMin <= 45) &&
            (eHour !== 22 || eMin === 0)) {

            // Set the formatted time as a chip
            const timeRange = `${startHour}:${startMin} - ${endHour}:${endMin}`;
            setChip(timeRange);

            // Also update the inputs array for consistency
            setInputs([startHour, startMin, endHour, endMin]);

            if (data && data.therapist) {
                if (!data.therapist.changed) {
                    data.therapist.old = { ...data.therapist };
                }
                data.therapist.value = timeRange;
                data.therapist.availability = {
                    start: `${startHour}:${startMin}`,
                    end: `${endHour}:${endMin}`,
                };
                data.therapist.changed = true;
                console.log({ data });
                onChange(data.therapist, data.dateIndex);
            }
            return;
        }
    }
};

const createHandleDelete = (
    setChip,
    setInputs,
    data,
    onChange
) => () => {
    setChip(null);
    setInputs(['', '', '', '']);
    if (data && data.therapist) {
        if (!data.therapist.changed) {
            data.therapist.old = { ...data.therapist };
        }
        data.therapist.value = null;
        data.therapist.availability = null;
        data.therapist.changed = true;
        onChange(data.therapist, data.dateIndex);
    }
};

const createCopyToClipboard = (chip) => () => {
    if (chip) {
        navigator.clipboard.writeText(chip)
            .then(() => { })
            .catch(err => {
                console.error('Error copying to clipboard', err);
            });
    }
};

const CustomTimeInputField = memo(({ id, onChange, data }) => {
    const [inputs, setInputs] = useState(['', '', '', '']);
    const [chip, setChip] = useState(null);
    const inputRefs = [useRef(), useRef(), useRef(), useRef()];

    // Function to format time from inputs array
    const formatTimeFromInputs = useCallback(() => {
        if (inputs.every(input => input.length > 0)) {
            return `${inputs[0]}:${inputs[1]} - ${inputs[2]}:${inputs[3]}`;
        }
        return null;
    }, [inputs]);

    // Create a reusable TimeInput component
    const TimeInput = useMemo(() => memo(({ value, onChange, onPaste, inputRef, maxLength = 2 }) => (
        <input
            type='text'
            ref={inputRef}
            value={value}
            onChange={onChange}
            onPaste={onPaste}
            style={{ width: '30px', height: '30px', textAlign: 'center' }}
            maxLength={maxLength}
            inputMode="numeric"
            pattern="[0-9]*"
        />
    )), []);

    // Validation function
    const validateTime = useCallback((hour, minute, isStart, start = null, startM = null) => {
        const h = parseInt(hour);
        const m = parseInt(minute);

        if (isStart) {
            return h >= 8 && h <= 21 && m >= 0 && m <= 59 && (h !== 21 || m <= 45);
        } else {
            const startHour = parseInt(start ?? inputs[0]);
            const startMin = parseInt(startM ?? inputs[1]);
            return h >= 8 && h <= 22 && m >= 0 && m <= 59 &&
                (h > startHour || (h === startHour && m >= startMin + 15)) &&
                (h !== 22 || m === 0);
        }
    }, [inputs]);

    const formatTimeValue = useCallback((value) => {
        return value.padStart(2, '0');
    }, []);

    // Create event handlers using the factory functions
    const handleInputChange = useCallback(
        createHandleInputChange(
            inputs,
            setInputs,
            inputRefs,
            validateTime,
            formatTimeValue,
            setChip,
            data,
            onChange
        ),
        [inputs, data, onChange, validateTime, formatTimeValue]
    );

    const handlePaste = useCallback(
        createHandlePaste(
            setChip,
            data,
            onChange,
            setInputs
        ),
        [data, onChange, setInputs]
    );

    const handleDelete = useCallback(
        createHandleDelete(setChip, setInputs, data, onChange),
        [data, onChange]
    );

    const copyToClipboard = useCallback(
        createCopyToClipboard(chip),
        [chip]
    );

    const draggableData = useMemo(() => ({
        timeData: data,
        value: chip || formatTimeFromInputs(),
        isStatus: STATUS_VALUES.includes(chip)
    }), [data, chip, formatTimeFromInputs]);

    const { attributes, listeners, setNodeRef, transform } = useDraggable({
        id: id || 'draggable-time-input',
        data: draggableData,
    });

    useEffect(() => {
        if (data && data.dateIndex && data.therapist) {
            const therapistCalendar = data.therapist?.value;
            if (therapistCalendar && therapistCalendar.length > 1 && therapistCalendar !== 'N/A') {
                for (const [key, value] of Object.entries(STATUS_MAPPINGS)) {
                    if (value.toUpperCase() === therapistCalendar.toUpperCase()) {
                        handleInputChange(0, key);
                        return;
                    }
                }
                if (therapistCalendar.includes(' - ')) {
                    let times = therapistCalendar.split(' - ');
                    times = [times[0].split(':'), times[1].split(':')];
                    const timeString = `${times[0][0]}:${times[0][1]} - ${times[1][0]}:${times[1][1]}`;
                    setChip(timeString);
                }
            }
        }
    }, [data, handleInputChange]);

    // useEffect(() => {
    //     if (chip === null) {
    //         inputRefs[0].current.focus();
    //     }
    // }, [chip]);

    // Create memoized handlers directly
    const handleChange0 = useCallback((e) => handleInputChange(0, e.target.value), [handleInputChange]);
    const handleChange1 = useCallback((e) => handleInputChange(1, e.target.value), [handleInputChange]);
    const handleChange2 = useCallback((e) => handleInputChange(2, e.target.value), [handleInputChange]);
    const handleChange3 = useCallback((e) => handleInputChange(3, e.target.value), [handleInputChange]);

    const handlePaste0 = useCallback((e) => handlePaste(0, e), [handlePaste]);
    const handlePaste1 = useCallback((e) => handlePaste(1, e), [handlePaste]);
    const handlePaste2 = useCallback((e) => handlePaste(2, e), [handlePaste]);
    const handlePaste3 = useCallback((e) => handlePaste(3, e), [handlePaste]);

    // Render function
    if (chip) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center' }}
                ref={setNodeRef}
                {...attributes}
                {...listeners}
                style={transform ? {
                    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
                    zIndex: 1000,
                } : undefined}>
                <Chip
                    label={chip}
                    onDelete={handleDelete}
                    color={data.therapist.changed ? 'secondary' : 'primary'}
                />
                <button onClick={copyToClipboard} size="small">
                    <FileCopyIcon />
                </button>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TimeInput
                inputRef={inputRefs[0]}
                value={inputs[0]}
                onChange={handleChange0}
                onPaste={handlePaste0}
            />
            :
            <TimeInput
                inputRef={inputRefs[1]}
                value={inputs[1]}
                onChange={handleChange1}
                onPaste={handlePaste1}
            />
            -
            <TimeInput
                inputRef={inputRefs[2]}
                value={inputs[2]}
                onChange={handleChange2}
                onPaste={handlePaste2}
            />
            :
            <TimeInput
                inputRef={inputRefs[3]}
                value={inputs[3]}
                onChange={handleChange3}
                onPaste={handlePaste3}
            />
        </Box>
    );
});

export default CustomTimeInputField;